import type { ISerialize } from '@/objects/ISerialize';
import { ImportFormat } from './ImportFormat';
import { ContestantImportDefaultSettings } from './ContestantImportDefaultSettings';

export class ImportContent implements ISerialize {
    public Columns: string[];

    public readonly ErrorCode: number;

    public FileName: string | null;

    public Format: ImportFormat | null;

    public readonly Preview: string | null;

    public ProcessedTime: number;

    public RowCount: number;

    public readonly Rows: any[];

    public Settings: ContestantImportDefaultSettings | null;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        Columns?: string[];
        ErrorCode?: number;
        FileName?: string | null;
        Format?: ImportFormat | null;
        Preview?: string | null;
        ProcessedTime?: number;
        RowCount?: number;
        Rows?: any[];
        Settings?: ContestantImportDefaultSettings | null;
    }) {
        this.Columns = values.Columns ?? [];

        this.ErrorCode = values.ErrorCode ?? 0;

        this.FileName = values.FileName ?? '';

        this.Format = values.Format ?? null;

        this.Preview = values.Preview ?? '';

        this.ProcessedTime = values.ProcessedTime ?? 0;

        this.RowCount = values.RowCount ?? 0;

        this.Rows = values.Rows ?? [];

        this.Settings = values.Settings ?? null;
    }

    public serialize(): string {
        return (
            '{' +
            `"Columns": [ ${this.Columns.map((x) => JSON.stringify(x))} ]` +
            ',' +
            `"ErrorCode": ${JSON.stringify(this.ErrorCode)}` +
            ',' +
            `"FileName": ${JSON.stringify(this.FileName)}` +
            ',' +
            `"Format": ${!this.Format ? null : this.Format?.serialize()}` +
            ',' +
            `"Preview": ${JSON.stringify(this.Preview)}` +
            ',' +
            `"ProcessedTime": ${JSON.stringify(this.ProcessedTime)}` +
            ',' +
            `"RowCount": ${JSON.stringify(this.RowCount)}` +
            ',' +
            `"Rows": [ ${this.Rows.map((x) => JSON.stringify(x))} ]` +
            ',' +
            `"Settings": ${!this.Settings ? null : this.Settings?.serialize()}` +
            '}'
        );
    }

    /**
     * Creates an instance of ImportContent from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        Columns?: string[];
        FileName?: string | null;
        Format?: ImportFormat | null;
        ProcessedTime?: number;
        RowCount?: number;
        Rows?: any[];
        Settings?: ContestantImportDefaultSettings | null;
    }): ImportContent {
        return new ImportContent(values);
    }

    public static _FromServerJson(json: any): ImportContent {
        return new ImportContent({
            Columns: json.Columns,
            ErrorCode: json.ErrorCode,
            FileName: json.FileName ?? '',
            Format: json.Format ? ImportFormat._FromServerJson(json.Format) : null,
            Preview: json.Preview ?? '',
            ProcessedTime: json.ProcessedTime,
            RowCount: json.RowCount,
            Rows: json.Rows,
            Settings: json.Settings ? ContestantImportDefaultSettings._FromServerJson(json.Settings) : null,
        });
    }

    /**
     * Creates an instance of ImportContent from another instance of ImportContent.
     * @param other The ImportContent you want to clone
     * @returns A cloned ImportContent
     */
    public static FromOther(other: ImportContent): ImportContent {
        return ImportContent._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ImportContent.FromOther(this);
    }

    static NAME_OF_CLASS = 'ImportContent';
}
